import { alpha, Chip, SvgIconProps } from '@mui/material';
import {
  DeliveryPhase,
  DeliveryState,
  PaymentState,
  Workflow,
} from '../../../enums/orders';
import ReplayIcon from '@mui/icons-material/Replay';
import { DeliveryBatchState } from '../../../enums/tasks';
import DoneIcon from '@mui/icons-material/Done';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
  AccessTime,
  LocalShipping,
  CheckCircle,
  Cancel,
  Warehouse,
  DirectionsBike,
  ReportProblem,
  LocalMall,
  AssignmentTurnedIn,
  Inventory,
  Hail,
  AddTask,
  AssignmentInd,
  DirectionsCar,
  QrCodeScanner,
  Store,
  Error,
} from '@mui/icons-material';

import {
  MoneyOff,
  HourglassEmpty,
  AttachMoney,
  PendingActions,
  Paid,
} from '@mui/icons-material';

import {
  PostAdd,
  AssignmentReturn,
  TakeoutDining,
  AirportShuttle,
  ErrorOutline,
  KeyboardReturn,
  HelpOutline,
} from '@mui/icons-material';

import {
  blue,
  teal,
  lightBlue,
  amber,
  green,
  grey,
  orange,
  red,
} from '@mui/material/colors';
import { chipOpacity, chipPadding } from './common';

export const getDeliveryStateColor = (state: DeliveryState): string => {
  switch (state) {
    case DeliveryState.CREATED:
      return alpha(blue[500], chipOpacity);
    case DeliveryState.PICKUP_REQUESTED:
      return alpha(teal[500], chipOpacity);
    case DeliveryState.PICKUP_ASSIGNED:
      return alpha(lightBlue[500], chipOpacity);
    case DeliveryState.SCANNED_FOR_PICKUP:
      return alpha(amber[500], chipOpacity);
    case DeliveryState.PICKED_UP:
      return alpha(green[300], chipOpacity);
    case DeliveryState.AT_WAREHOUSE:
      return alpha(grey[500], chipOpacity);
    case DeliveryState.DELIVERY_ASSIGNED:
      return alpha(blue[300], chipOpacity);
    case DeliveryState.SCANNED_FOR_DELIVERY:
      return alpha(orange[300], chipOpacity);
    case DeliveryState.PICKED_UP_FOR_DELIVERY:
      return alpha(green[400], chipOpacity);
    case DeliveryState.IN_TRANSIT:
      return alpha(orange[500], chipOpacity);
    case DeliveryState.DELIVERED:
      return alpha(green[500], chipOpacity);
    case DeliveryState.DELIVERED_AT_WAREHOUSE:
      return alpha(teal[300], chipOpacity);
    case DeliveryState.AWAITING_RETURN:
      return alpha(orange[700], chipOpacity);
    case DeliveryState.CANCELLED:
      return alpha(red[700], chipOpacity);
    default:
      return alpha(grey[700], chipOpacity);
  }
};

export const getDeliveryStateIcon = (
  state: DeliveryState,
  styleOverrides?: SvgIconProps
): JSX.Element => {
  let iconProps: SvgIconProps = { fontSize: 'small', color: 'inherit' };

  if (styleOverrides) {
    iconProps = { ...iconProps, ...styleOverrides };
  }

  switch (state) {
    case DeliveryState.CREATED:
      return <PostAdd {...iconProps} />; // Better for "order created"
    case DeliveryState.PICKUP_REQUESTED:
      return <AssignmentReturn {...iconProps} />; // Indicates a pickup request
    case DeliveryState.PICKUP_ASSIGNED:
      return <AssignmentInd {...iconProps} />; // Good for assignment
    case DeliveryState.SCANNED_FOR_PICKUP:
      return <QrCodeScanner {...iconProps} />; // Perfect for scanning
    case DeliveryState.PICKED_UP:
      return <LocalShipping {...iconProps} />; // Best for picked up items
    case DeliveryState.AT_WAREHOUSE:
      return <Warehouse {...iconProps} />; // Perfect
    case DeliveryState.DELIVERY_ASSIGNED:
      return <AssignmentTurnedIn {...iconProps} />; // Indicates completed assignment
    case DeliveryState.SCANNED_FOR_DELIVERY:
      return <QrCodeScanner {...iconProps} />; // More specific than QR code
    case DeliveryState.PICKED_UP_FOR_DELIVERY:
      return <TakeoutDining {...iconProps} />; // Represents "taking out for delivery"
    case DeliveryState.IN_TRANSIT:
      return <AirportShuttle {...iconProps} />; // Better for transit
    case DeliveryState.DELIVERED:
      return <CheckCircle {...iconProps} />; // Perfect for completion
    case DeliveryState.DELIVERED_AT_WAREHOUSE:
      return <Store {...iconProps} />; // Good for warehouse delivery
    case DeliveryState.CANCELLED:
      return <Cancel {...iconProps} />; // Standard cancel icon
    case DeliveryState.AWAITING_RETURN:
      return <KeyboardReturn {...iconProps} />; // Better for returns
    default:
      return <HelpOutline {...iconProps} />; // Question mark for unknown states
  }
};
export const showDeliveryState = (state: DeliveryState): JSX.Element => {
  return (
    <Chip
      icon={getDeliveryStateIcon(state)}
      label={<b>{state.toString().replaceAll('_', ' ')}</b>}
      variant="filled"
      size="small"
      style={{
        margin: 4,
        padding: chipPadding,
        backgroundColor: getDeliveryStateColor(state),
      }}
    />
  );
};

export const phaseColors: Record<DeliveryPhase, string> = {
  [DeliveryPhase.AWAITING_ACTION]: blue[500],
  [DeliveryPhase.PICKUP]: orange[500],
  [DeliveryPhase.DELIVERY]: green[500],
  [DeliveryPhase.CANCELLED]: red[500],
};

export function getPhaseStateColor(
  baseColor: string,
  index: number,
  total: number
): string {
  // More distinct strengths, ev enly spaced
  const distinctStrengths =
    total <= 5
      ? [0.25, 0.45, 0.65, 0.85, 1.0].slice(0, total)
      : Array.from({ length: total }, (_, i) => 0.2 + (0.8 * i) / (total - 1));
  return alpha(baseColor, distinctStrengths[index]);
}

export const getPaymentStateIcon = (state: PaymentState): JSX.Element => {
  const iconProps: SvgIconProps = { fontSize: 'small', color: 'inherit' };

  switch (state) {
    case PaymentState.UNPAID:
      return <MoneyOff {...iconProps} />; // No money received
    case PaymentState.PENDING_PAYMENT_BY_DRIVER:
      return <HourglassEmpty {...iconProps} />; // Waiting for driver payment
    case PaymentState.PAID_BY_DRIVER:
      return <AttachMoney {...iconProps} />; // Money received from driver
    case PaymentState.PENDING_PAYMENT_TO_MERCHANT:
      return <PendingActions {...iconProps} />; // Waiting to pay merchant
    case PaymentState.PAID_TO_MERCHANT:
      return <Paid {...iconProps} />; // Payment completed to merchant
    default:
      return <HelpOutline {...iconProps} />; // Fallback for unknown states
  }
};

export const getPaymentStateColor = (state: PaymentState): string => {
  const opacity = 0.5;
  switch (state) {
    case PaymentState.UNPAID:
      return `rgba(255, 248, 220, ${opacity})`; // Pale gold (light, subtle)
    case PaymentState.PENDING_PAYMENT_BY_DRIVER:
      return `rgba(255, 228, 150, ${opacity})`; // Soft gold with warmth
    case PaymentState.PAID_BY_DRIVER:
      return `rgba(255, 215, 0, ${opacity})`; // Standard gold (bold, central tone)
    case PaymentState.PENDING_PAYMENT_TO_MERCHANT:
      return `rgba(218, 165, 32, ${opacity})`; // Rich gold (GoldenRod)
    case PaymentState.PAID_TO_MERCHANT:
      return `rgba(184, 134, 11, ${opacity})`; // Deep antique gold
    default:
      return `rgba(169, 169, 169, ${opacity})`; // Fallback gray
  }
};

export const showPaymentState = (state: PaymentState): JSX.Element => {
  const getStateLabel = (state: PaymentState): string => {
    switch (state) {
      case PaymentState.UNPAID:
        return 'Unpaid';
      case PaymentState.PENDING_PAYMENT_BY_DRIVER:
        return 'Pending (Driver)';
      case PaymentState.PAID_BY_DRIVER:
        return 'Paid by Driver';
      case PaymentState.PENDING_PAYMENT_TO_MERCHANT:
        return 'Pending (Merchant)';
      case PaymentState.PAID_TO_MERCHANT:
        return 'Paid to Merchant';
    }
  };

  return (
    <Chip
      label={<b>{getStateLabel(state)}</b>}
      variant="filled"
      style={{
        margin: 1,
        backgroundColor: getPaymentStateColor(state),
        textTransform: 'capitalize',
      }}
    />
  );
};
